import { Component, inject, OnInit, OnDestroy, signal, ViewChild} from '@angular/core';
import { Setting, Map } from '../service/settings.service';
import { cloneDeep } from 'lodash';
import { ToastService } from '../service/toast.service';
import { CommonModule, KeyValue } from '@angular/common';
import { Backend } from '../backend-interface/backend';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../service/translation.service';
import { NgbAccordionDirective, NgbAccordionItem, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { ToastComponent } from '../components/toast/toast.component';
import { LoadingErrorComponent } from '../components/loading-error/loading-error.component';
import { FormsModule } from '@angular/forms';
import { EditFormComponent } from './edit-form/edit-form.component';
import { DeleteFormComponent } from './delete-form/delete-form.component';
import { AddValueFormComponent } from './add-value-form/add-value-form.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

/**
 * Composant which manage all console settings
 */
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    NgbAccordionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    ToastComponent,
    LoadingErrorComponent,
    CommonModule,
    FormsModule,
    EditFormComponent,
    DeleteFormComponent,
    AddValueFormComponent,
    NgbAccordionDirective
  ],
  providers: [ToastService, TranslationService]
})
export class SettingsComponent implements OnInit, OnDestroy {
  readonly lSetting = signal<Setting[]>([]);
  readonly lAllSetting = signal<Setting[]>([]);
  readonly setsearch = signal('');
  readonly loadingOerror = signal(true);
  readonly preId = signal('');
  readonly settingSelect = signal<Setting | undefined>(undefined);

  readonly toastService = inject(ToastService);
  readonly translationService = inject(TranslationService);

  @ViewChild('settingsItem') set settingsItemRef(item: NgbAccordionItem | undefined) {
    if (item && this.settingsItem !== item) {
    this.settingsItem = item;
    this.settingsItem.disabled = true;
    if (this.settingsItem.collapsed) {
      this.settingsItem.toggle();
    }
  }
  }
  private settingsItem?: NgbAccordionItem;


  ngOnInit(): void {
   
    this.getAllSettings();
  }

  ngOnDestroy(): void {
    this.toastService.clear();
  }


  async getAllSettings(): Promise<void> {
    this.loadingOerror.set(true);
    const listeS = await Backend.SETTINGS.getAll();
    for (const setting of listeS) {
      if (typeof setting.setting_value === 'string') {
        try {
          setting.setting_value = JSON.parse(setting.setting_value);
        } catch (e) {
          this.toastService.show(`${setting.setting_name} : ${e}`, { classname: 'bg-danger', delay: 6000 });
        }
      }
    }
    this.lAllSetting.set(listeS);
    this.lSetting.set(cloneDeep(listeS));
    this.loadingOerror.set(false);

  }

  typeofValue(value: string | string[] | Map<string> | undefined): string {
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'string') return 'string';
    return 'map';
  }

  getString(value: string | string[] | Map<string> | undefined): string {
    return typeof value === 'string' ? value : '';
  }

  getArray(value: string | string[] | Map<string> | undefined): string[] {
    return Array.isArray(value) ? value : [];
  }

  sliceValue(value: string, list: string[]): string[] {
    const cloneList = cloneDeep(list);
    const indexValue = cloneList.indexOf(value);
    if (indexValue > -1) cloneList.splice(indexValue, 1);
    return cloneList;
  }

  getMap(value: string | string[] | Map<string> | undefined): Map<string> {
    return value && !Array.isArray(value) && typeof value !== 'string' ? value : {};
  }

  getLengthOfMap(value: Map<string>): number {
    return Object.keys(value).length;
  }

  mapKeyToList(map: Map<string>): string[] {
    return map ? Object.keys(map) : [];
  }

  originalOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => 0;

  search(value: string): void {
    const lowerValue = value.toLowerCase();
    const allSettings = this.lAllSetting();
    const array = allSettings.filter(val => val.setting_name?.toLowerCase().includes(lowerValue));
    if (array) this.lSetting.set(cloneDeep(array));

    const listbystring = cloneDeep(allSettings.filter(val => this.getValueString(val.setting_value, lowerValue)));
    const listbylist = cloneDeep(allSettings.filter(val => this.getListFilter(val, lowerValue)));
    const listbymap = cloneDeep(allSettings.filter(val => this.getMapSearch(val, lowerValue)));

    if (this.lSetting().length < 1) {
      this.lSetting.set((listbystring ?? []).concat(listbylist ?? [], listbymap ?? []));
      this.formatListSearch(value);
    }
  }

  getValueString(value: string | string[] | Map<string> | undefined, search: string): boolean {
    return typeof value === 'string' && value.toLowerCase().includes(search);
  }

  getListFilter(sett: Setting, search: string): boolean {
    return Array.isArray(sett.setting_value)
      ? sett.setting_value.some(v => v.toLowerCase().includes(search))
      : false;
  }

  getMapSearch(sett: Setting, search: string): boolean {
    if (sett.setting_value && !Array.isArray(sett.setting_value) && typeof sett.setting_value !== 'string') {
      return Object.entries(sett.setting_value).some(
        ([k, v]) => k.toLowerCase().includes(search) || v.toLowerCase().includes(search)
      );
    }
    return false;
  }

  formatListSearch(text: string): void {
    const settings = this.lSetting();
    for (const sett of settings) {
      if (Array.isArray(sett.setting_value)) {
        sett.setting_value = sett.setting_value.filter(v => v.toLowerCase().includes(text));
      }
      if (sett.setting_value && !Array.isArray(sett.setting_value) && typeof sett.setting_value !== 'string') {
        const entries = Object.entries(sett.setting_value).filter(
          ([k, v]) => k.toLowerCase().includes(text) || v.toLowerCase().includes(text)
        );
        sett.setting_value = {};
        for (const [k, v] of entries) {
          sett.setting_value[k] = v;
        }
      }
    }
    this.lSetting.set(settings);
  }

  resetL(): void {
    if (this.setsearch() === '') {
      this.lSetting.set(cloneDeep(this.lAllSetting()));
    }
  }


  private async patchSettingValue(elem: Setting, toastMsg: string): Promise<void> {
    elem.setting_value = JSON.stringify(elem.setting_value);
    const body = { setting_value: elem.setting_value, description: elem.description };
    try {
      await Backend.SETTINGS.patchValue(elem.setting_name, body);
      if (toastMsg) {
        this.translationService.get(toastMsg).subscribe((msg) => {
          this.toastService.show(msg, { classname: 'bg-success', delay: 7000 });
        });
      }
    } catch (error) {
      const err = error as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Error', { classname: 'bg-danger', delay: 6000 });
    }
    await this.getAllSettings();
  }

  async addValues(newvalues: string[] | string[][], name: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListA = allSettings.find(v => v.setting_name === name);
    if (!elemListA) return;

    if (Array.isArray(elemListA.setting_value)) {
      for (const v of newvalues) {
        if (typeof v === 'string') {
          elemListA.setting_value.push(v);
        }
      }
    } else if (elemListA.setting_value && typeof elemListA.setting_value !== 'string') {
      for (const v of newvalues) {
        elemListA.setting_value[v[0]] = v[1];
      }
    } else if (typeof elemListA.setting_value === 'string') {
      elemListA.setting_value = newvalues[0] as string;
    }

    await this.patchSettingValue(elemListA, 'setting-toast-add');
  }

  async addDescription(newdesc: string[] | string[][], name: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListA = allSettings.find(v => v.setting_name === name);
    if (elemListA && typeof newdesc[0] === 'string') {
      elemListA.description = newdesc[0];
      await this.patchSettingValue(elemListA, 'setting-toast-edit-description');
    }
  }

  async editValue(modified: string, name: string | undefined, currentvalue: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListA = allSettings.find(v => v.setting_name === name);
    if (!elemListA) return;

    if (Array.isArray(elemListA.setting_value) && currentvalue) {
      const index = elemListA.setting_value.indexOf(currentvalue);
      if (index > -1) elemListA.setting_value.splice(index, 1, modified);
    } else if (
      currentvalue &&
      elemListA.setting_value &&
      typeof elemListA.setting_value !== 'string' &&
      !Array.isArray(elemListA.setting_value)
    ) {
      elemListA.setting_value[currentvalue] = modified;
    } else if (typeof elemListA.setting_value === 'string') {
      elemListA.setting_value = modified;
    }

    await this.patchSettingValue(elemListA, currentvalue === modified ? 'setting-toast-edit-no-change' : 'setting-toast-edit-change');
  }

  async editDescription(modified: string, name: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListA = allSettings.find(v => v.setting_name === name);
    if (elemListA) {
      elemListA.description = modified;
      await this.patchSettingValue(elemListA, 'setting-toast-edit-description');
    }
  }

  async deleteValue(deleteValue: string, name: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListAll = allSettings.find(v => v.setting_name === name);
    if (!elemListAll) return;

    if (Array.isArray(elemListAll.setting_value)) {
      const index = elemListAll.setting_value.indexOf(deleteValue);
      if (index > -1) elemListAll.setting_value.splice(index, 1);
    } else if (elemListAll.setting_value && typeof elemListAll.setting_value !== 'string') {
      delete elemListAll.setting_value[deleteValue];
    } else if (typeof elemListAll.setting_value === 'string') {
      elemListAll.setting_value = '';
    }

    await this.patchSettingValue(elemListAll, 'setting-toast-delete');
  }

  async deleteDescription(deleteValue: string, name: string | undefined): Promise<void> {
    const allSettings = this.lAllSetting();
    const elemListAll = allSettings.find(v => v.setting_name === name);
    if (elemListAll) {
      elemListAll.description = '';
      await this.patchSettingValue(elemListAll, 'setting-toast-edit-description');
    }
  }

  async settingSelected(set: Setting): Promise<void> {
    this.settingSelect.set(set);
    const del = document.getElementById('actifElem');
    if (del) {
      del.removeAttribute('id');
      del.setAttribute('id', this.preId());
    }
    const el = document.getElementById('elem' + set.setting_name);
    if (el) {
      el.id = 'actifElem';
      this.preId.set('elem' + set.setting_name);
    }
    document.getElementById('settingValue')?.removeAttribute('hidden');
  }

  hideSettingDetails(): void {
    const elem = document.getElementById('settingValue');
    if (elem) {
      elem.removeAttribute('hidden');
      elem.setAttribute('hidden', 'hidden');
    }
  }

  scroolTo(): void {
    (document.getElementById('actifElem') as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
  }
}
